/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";
import { Grid, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import MessageBody from "../components/modules/messageBody";
import MessagesTab from "../components/modules/messagesTab";
import MessageCompose from "../components/modules/messageCompose";
import {
  getFacilityUsers,
  getReceivedMessages,
  getSentMessages,
} from "../services/messages.service";
import {
  ModalContainer,
  ModalInnerContainer,
} from "../constants/layout.constants";
import { isNullUndefined } from "../util/util";
import { useSelector } from "react-redux";

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [facilityUsersOptions, setFacilityUsersOptions] = useState([]);
  const [facilityOwners, setFacilityOwners] = useState([]);
  const [isHoaFacility, setIsHoaFacility] = useState(false);
  const [viewCompose, setViewCompose] = useState({
    status: false,
    newMessage: null,
    message: {},
  });

  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPageNo(0);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const body = { query: debouncedSearch };
    getReceivedMessages(pageNo, body).then((inboxData) =>
      setInbox(inboxData?.messages || [])
    );
    getSentMessages(pageNo, body).then((outboxData) =>
      setOutbox(outboxData?.messages || [])
    );
  }, [pageNo, user.proxyFacility, debouncedSearch]);

  useEffect(() => {
    getFacilityUsers().then((facilityUsers) => {
      if (!facilityUsers) {
        setFacilityUsersOptions([]);
        setIsHoaFacility(false);
        setFacilityOwners([]);
        return;
      }

      const facilityTenants = facilityUsers.facilityTenants || [];
      const facilityUsersList = facilityUsers.facilityUsers || [];

      let options = facilityTenants.concat(facilityUsersList);

      //Removes duplicate objects in the array based on the unique identifier for the object.
      function removeDuplicateObjects(arr, value) {
        const result = arr.filter(
          (item, index, val) =>
            index === val.findIndex((t) => t[value] === item[value])
        );

        return result;
      }

      const uniqueArray = removeDuplicateObjects(options, "value");

      if (isNullUndefined(facilityUsers.hoaFacility)) {
        setFacilityUsersOptions(uniqueArray || []);
      } else {
        setFacilityUsersOptions(facilityTenants);
      }

      if (!isNullUndefined(facilityUsers.hoaFacility)) {
        setIsHoaFacility(true);
        setFacilityOwners(facilityUsers.facilityOwners || []);
      } else {
        setIsHoaFacility(false);
        setFacilityOwners([]);
      }
    });
  }, [user.proxyFacility]);

  useEffect(() => {
    switch (currentTab) {
      case 0:
        setMessages(inbox);
        break;
      case 1:
        setMessages(outbox);
        break;
      default:
        setMessages(inbox);
        setCurrentTab(0);
        break;
    }
  }, [currentTab, inbox, outbox]);

  function handleMessageSelect(newSelect) {
    setSelectedMessage(newSelect);
    setViewCompose({ message: newSelect });
  }

  function handleSearch(e) {
    setSearch(e.target.value);
  }

  function changeTabs(newTab) {
    setCurrentTab(newTab);
  }

  return (
    <Grid
      container
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        width: "100%",
        height: "75vh",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "#fff",
          margin: "10px",
          borderRadius: "10px",
          padding: "0px 10px",
          width: { xs: "100%", lg: "33.33%" },
        }}
      >
        <Box
          sx={{
            marginTop: "20px",
            marginBottom: "10px",
            padding: "20px",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "flex-start",
          }}
        >
          <TextField
            size="small"
            fullwidth
            sx={{ width: "100%" }}
            label="Search Messages"
            value={search}
            onChange={handleSearch}
          />
        </Box>
        <MessagesTab
          inbox={inbox}
          outbox={outbox}
          currentTab={currentTab}
          changeTabs={changeTabs}
          selectMessage={handleMessageSelect}
          composeMessage={() =>
            setViewCompose({ status: true, newMessage: true, message: {} })
          }
        />
      </Box>
      <Box
        sx={{
          display: {
            xs: "none",
            lg: "flex",
          },
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
          margin: "10px",
          borderRadius: "10px",
          width: { xs: "100%", lg: "66.67%" },
        }}
      >
        {viewCompose.status ? (
          <MessageCompose
            unsetView={() =>
              setViewCompose({ status: false, newMessage: false, message: {} })
            }
            message={viewCompose.message ? viewCompose.message : {}}
            newMessage={viewCompose.newMessage}
            inbox={inbox}
            outbox={outbox}
            setOutBox={setOutbox}
            recipients={facilityUsersOptions}
            owners={facilityOwners}
            isHoaFacility={isHoaFacility}
          />
        ) : selectedMessage ? (
          <MessageBody
            viewCompose={viewCompose}
            updateCompose={setViewCompose}
            message={selectedMessage}
            composeMessage={() =>
              setViewCompose({
                status: true,
                newMessage: false,
                message: selectedMessage,
              })
            }
          />
        ) : (
          <Box
            sx={{
              display: { xs: "none", lg: "inherit" },
            }}
          >
            <Typography
              sx={{
                width: "100%",
                color: "rgba(0,0,0,0.5)",
                alignText: "center",
                fontSize: "1rem",
                fontWeight: 800,
              }}
            >
              Select a chat on the sidebar to view it here
            </Typography>
          </Box>
        )}
      </Box>

      <ModalContainer
        open={selectedMessage}
        onClose={() => setSelectedMessage()}
        sx={{ display: { xs: "inherit", lg: "none" } }}
      >
        <ModalInnerContainer sx={{ padding: 0 }}>
          <MessageBody
            viewCompose={viewCompose}
            updateCompose={setViewCompose}
            message={selectedMessage}
            composeMessage={() =>
              setViewCompose({
                status: true,
                newMessage: false,
                message: selectedMessage,
              })
            }
          />
        </ModalInnerContainer>
      </ModalContainer>
      <ModalContainer
        open={viewCompose.status}
        onClose={() => setSelectedMessage()}
        sx={{ display: { xs: "inherit", lg: "none" } }}
      >
        <ModalInnerContainer sx={{ padding: 0 }}>
          <MessageCompose
            unsetView={() =>
              setViewCompose({ status: false, newMessage: false, message: {} })
            }
            message={viewCompose.message ? viewCompose.message : {}}
            newMessage={viewCompose.newMessage}
            inbox={inbox}
            outbox={outbox}
            setOutBox={setOutbox}
            recipients={facilityUsersOptions}
          />
        </ModalInnerContainer>
      </ModalContainer>
    </Grid>
  );
}
